import {Injectable, Injector} from '@angular/core';
import {Observable, BehaviorSubject} from 'rxjs';
import {tap, combineLatestWith, map} from 'rxjs/operators';
import {
    ConfirmWebshopOrderInputDto, IConfirmWebshopOrderInputDto,
    OrderGuideServiceProxy,
    OrderGuideSimpleDto,
    OrderGuideTemplateDto, PagedResultDtoOfArticleDto,
    PagedResultDtoOfOrderGuideTemplateDto,
    WebshopSendBasketOutput,
    WebShopServiceProxy,
} from '@shared/service-proxies/service-proxies';
import {OrderState, OrderStateService} from '@app/webshop/field-agent/shared/services/order-state.service';
import {WebshopService} from '@app/shared/common/shop-system/services/webshop.service';
import {BasePermissionDefinition} from '@shared/permission/base-permission-definition';
import {PermissionCheckerService} from '@node_modules/abp-ng2-module';
import {ImpersonationService} from '@app/admin/users/impersonation.service';
import {Router} from '@angular/router';
import Swal from 'sweetalert2';
import {ConfirmService} from '@app/webshop/field-agent/shared/services/three-state-confirm.service';

@Injectable({
    providedIn: 'root',
})
export class FieldAgentOrderService {

    private fieldAgentDashboard = '/app/webshop/field-agent/select-templates';
    private selectPromotion = '/app/webshop/field-agent/promotion';
    private basketOverview = '/app/webshop/basket-overview';
    private selectProducts = '/app/webshop/articles/';
    private orderConfirmation = '/app/webshop/field-agent/order-confirmation';

    private readonly targetCacheKey = 'target'; // Key for ABP cache or session storage
    private readonly orderScheduleIdCacheKey = 'orderScheduleId'; // Key for ABP cache or session storage
    private readonly selectedBrandCacheKey = 'selectedBrand'; // Key for ABP cache or session storage
    private readonly selectedTemplateCacheKey = 'selectedTemplate'; // Key for ABP cache or session storage
    private readonly totalOrderAmountCacheKey = 'orderTotalAmount'; // Key for ABP cache or session storage

    protected confirmService: ConfirmService;
    protected permissionChecker: PermissionCheckerService;
    protected impersonationService: ImpersonationService;
    protected router: Router;

    private webShopServiceProxy: WebShopServiceProxy;
    private orderGuideServiceProxy: OrderGuideServiceProxy;
    private orderStateService: OrderStateService;
    private webShopService: WebshopService;

    private _brands$: BehaviorSubject<OrderGuideSimpleDto[]>;
    private _selectedBrand$: BehaviorSubject<OrderGuideSimpleDto>;
    private _orderGuidesTemplates$: BehaviorSubject<OrderGuideTemplateDto[]>;
    private _selectedOrderGuideTemplate$: BehaviorSubject<OrderGuideTemplateDto>;
    private _selectedTarget$: BehaviorSubject<string>;
    private _orderScheduleId$: BehaviorSubject<string>;
    private _totalOrderAmount$: BehaviorSubject<string>;

    private _selectedBrand: OrderGuideSimpleDto | undefined = undefined;
    get selectedBrand(): OrderGuideSimpleDto | undefined {
        if (!this._selectedBrand) {
            this._selectedBrand = this.selectedBrandValue;
        }

        return this._selectedBrand;
    }

    set selectedBrand(value: OrderGuideSimpleDto | undefined) {
        this._selectedBrand = value;
        this.selectBrand(value);
    }

    private _selectedOrderGuideTemplate: OrderGuideTemplateDto | undefined = undefined;
    get selectedOrderGuideTemplate(): OrderGuideTemplateDto | undefined {
        if (!this._selectedOrderGuideTemplate) {
            this._selectedOrderGuideTemplate = this.selectedOrderGuideTemplatedValue;
        }

        return this._selectedOrderGuideTemplate;
    }

    set selectedOrderGuideTemplate(value: OrderGuideTemplateDto | undefined) {
        this._selectedOrderGuideTemplate = value;
        this.selectOrderGuideTemplate(value);
    }

    get orderPreparationReady$(): Observable<boolean> {
        return this.selectedBrand$.pipe(
            combineLatestWith(this.selectedOrderGuideTemplate$),
            map(([brand, template]) => !!brand && !!template)
        );
    }

    isAllowNavigateToOrderConfirmation = false;

    constructor(injector: Injector) {
        this.permissionChecker = injector.get(PermissionCheckerService);
        this.impersonationService = injector.get(ImpersonationService);
        this.router = injector.get(Router);
        this.confirmService = injector.get(ConfirmService);

        this.orderGuideServiceProxy = injector.get(OrderGuideServiceProxy);
        this.orderStateService = injector.get(OrderStateService);
        this.webShopServiceProxy = injector.get(WebShopServiceProxy);
        this.webShopService = injector.get(WebshopService);

        this._brands$ = new BehaviorSubject<OrderGuideSimpleDto[]>(undefined);
        this._selectedBrand$ = new BehaviorSubject<OrderGuideSimpleDto>(this._getSelectedBrand() ?? undefined);
        this._orderGuidesTemplates$ = new BehaviorSubject<OrderGuideTemplateDto[]>(undefined);
        this._selectedOrderGuideTemplate$ = new BehaviorSubject<OrderGuideTemplateDto>(this._getOrderGuideTemplate() ?? undefined);
        this._selectedTarget$ = new BehaviorSubject<string>(this._getTarget() ?? undefined);
        this._orderScheduleId$ = new BehaviorSubject<string>(this._getOrderScheduleId() ?? undefined);
        this._totalOrderAmount$ = new BehaviorSubject<string>(this._getOrderTotalAmount() ?? undefined);
    }

    flush() {
        this.resetTarget();
        this.resetOrderScheduleId();
        this.resetOrderTotalAmount();
        this.reset();
    }

    //#region OrderTotalAmount
    get orderTotalAmount$(): Observable<string> {
        return this._totalOrderAmount$.asObservable();
    }

    get orderTotalAmountValue(): string {
        return this._totalOrderAmount$.getValue();
    }

    setOrderTotalAmount(result: string): void {
        this._totalOrderAmount$.next(result);
        this._setOrderTotalAmount(result);
    }

    resetOrderTotalAmount(): void {
        abp.utils.setCookieValue(this.totalOrderAmountCacheKey, '', new Date(0), abp.appPath);
        sessionStorage.removeItem(this.totalOrderAmountCacheKey);

        this._totalOrderAmount$.next(undefined);
    }

    //#region Cache
    private _getOrderTotalAmount(): string | undefined {
        const totalAmount = abp.utils.getCookieValue(this.totalOrderAmountCacheKey) || sessionStorage.getItem(this.totalOrderAmountCacheKey);
        return totalAmount || undefined;
    }

    private _setOrderTotalAmount(totalAmount: string): void {
        abp.utils.setCookieValue(this.totalOrderAmountCacheKey, totalAmount, null, abp.appPath);
        sessionStorage.setItem(this.totalOrderAmountCacheKey, totalAmount);
    }
    //#endregion Cache
    //#endregion OrderTotalAmount

    //#region OrderScheduleId
    get orderScheduleId$(): Observable<string> {
        return this._orderScheduleId$.asObservable();
    }

    get orderScheduleIdValue(): string {
        return this._orderScheduleId$.getValue();
    }

    setOrderScheduleId(result: string): void {
        this._orderScheduleId$.next(result);
        this._setOrderScheduleId(result);
    }

    resetOrderScheduleId(): void {
        abp.utils.setCookieValue(this.orderScheduleIdCacheKey, '', new Date(0), abp.appPath);
        sessionStorage.removeItem(this.orderScheduleIdCacheKey);

        this._orderScheduleId$.next(undefined);
    }

    //#region Cache
    private _getOrderScheduleId(): string | undefined {
        const cachedState = abp.utils.getCookieValue(this.orderScheduleIdCacheKey) || sessionStorage.getItem(this.orderScheduleIdCacheKey);
        return cachedState || undefined;
    }

    private _setOrderScheduleId(id: string): void {
        abp.utils.setCookieValue(this.orderScheduleIdCacheKey, id, null, abp.appPath);
        sessionStorage.setItem(this.orderScheduleIdCacheKey, id);
    }
    //#endregion Cache
    //#endregion OrderScheduleId

    //#region Target
    get selectedTarget$(): Observable<string> {
        return this._selectedTarget$.asObservable();
    }

    get selectedTargetValue(): string {
        return this._selectedTarget$.getValue();
    }

    setTarget(result: string): void {
        this._selectedTarget$.next(result);
        this._setTarget(result);
    }

    resetTarget(): void {
        abp.utils.setCookieValue(this.targetCacheKey, '', new Date(0), abp.appPath);
        sessionStorage.removeItem(this.targetCacheKey);

        this._selectedTarget$.next(undefined);
    }

    //#region Cache
    private _getTarget(): string | undefined {
        const cachedState = abp.utils.getCookieValue(this.targetCacheKey) || sessionStorage.getItem(this.targetCacheKey);
        return cachedState || undefined;
    }

    private _setTarget(target: string): void {
        abp.utils.setCookieValue(this.targetCacheKey, target, null, abp.appPath);
        sessionStorage.setItem(this.targetCacheKey, target);
    }
    //#endregion Cache

    //#endregion Target

    //#region Brands
    get brands$(): Observable<OrderGuideSimpleDto[]> {
        return this._brands$.asObservable();
    }

    get brandsValue(): OrderGuideSimpleDto[] {
        return this._brands$.getValue();
    }

    get selectedBrand$(): Observable<OrderGuideSimpleDto> {
        return this._selectedBrand$.asObservable();
    }

    get selectedBrandValue(): OrderGuideSimpleDto {
        return this._selectedBrand$.getValue();
    }

    get selectedBrandId(): string | undefined {
        return this._selectedBrand?.id;
    }


    setBrands(result: OrderGuideSimpleDto[]): void {
        //update items
        this._brands$.next(result);
    }

    selectBrand(selectedBrand: OrderGuideSimpleDto): void {
        if (!this._selectedBrand || this._selectedBrand.id !== selectedBrand.id) {
            this._selectedBrand = selectedBrand;
            this._selectedBrand$.next(selectedBrand);
            this._setSelectedBrand(selectedBrand);

            return;
        }

        this.unselectBrand();
    }

    //#region Cache
    private _getSelectedBrand(): OrderGuideSimpleDto | undefined {
        const brand = abp.utils.getCookieValue(this.selectedBrandCacheKey) || sessionStorage.getItem(this.selectedBrandCacheKey);
        return JSON.parse(brand) as OrderGuideSimpleDto || undefined;
    }

    private _setSelectedBrand(brand: OrderGuideSimpleDto): void {
        const cachedBrand = JSON.stringify(brand);
        abp.utils.setCookieValue(this.selectedBrandCacheKey, cachedBrand, null, abp.appPath);
        sessionStorage.setItem(this.selectedBrandCacheKey, cachedBrand);
    }
    //#endregion Cache

    unselectBrand(): void {
        abp.utils.setCookieValue(this.selectedBrandCacheKey, '', new Date(0), abp.appPath);
        sessionStorage.removeItem(this.selectedBrandCacheKey);

        this._selectedBrand = undefined;
        this._selectedBrand$.next(undefined);
    }
    //#endregion Brands

    get orderGuideTemplates$(): Observable<OrderGuideTemplateDto[]> {
        return this._orderGuidesTemplates$.asObservable();
    }

    get selectedOrderGuideTemplate$(): Observable<OrderGuideTemplateDto> {
        return this._selectedOrderGuideTemplate$.asObservable();
    }

    get selectedOrderGuideTemplatedValue(): OrderGuideTemplateDto {
        return this._selectedOrderGuideTemplate$.getValue();
    }

    //#region Cache
    private _getOrderGuideTemplate(): OrderGuideTemplateDto | undefined {
        const template = abp.utils.getCookieValue(this.selectedTemplateCacheKey) || sessionStorage.getItem(this.selectedTemplateCacheKey);
        return JSON.parse(template) as OrderGuideTemplateDto || undefined;
    }

    private _setOrderGuideTemplate(template: OrderGuideTemplateDto): void {
        const cachedTemplate = JSON.stringify(template);
        abp.utils.setCookieValue(this.selectedTemplateCacheKey, cachedTemplate, null, abp.appPath);
        sessionStorage.setItem(this.selectedTemplateCacheKey, cachedTemplate);
    }
    //#endregion Cache

    reset() {
        this.orderStateService.selectTemplates();

        this._brands$.next(undefined);
        this.selectedBrand = undefined;
        this.selectedOrderGuideTemplate = undefined;
        this.unselectBrand();
        this.unselectOrderGuideTemplate();
        this.webShopService.clear();
        this.orderStateService.reset();
    }


    clearAllSelections(selectedBrand: OrderGuideSimpleDto): void {
        if ((!this._selectedBrand || this._selectedBrand.id !== selectedBrand.id)
            && this.orderStateService.getCurrentState() !== OrderState.SelectTemplates) {
            abp.message.confirm(
                'If you want to change brand or template, do you want to cancel its cause to clearing everything?'
                , 'Clear selection'
                , (result: boolean) => {
                    if (result) {

                    }
                }
                , {
                    showCloseButton: true,
                    customClass: {
                        container: 'modal-like'
                    }
                });
        }
    }

    getUserOrderGuides$(): Observable<OrderGuideSimpleDto[]> {
        return this.orderGuideServiceProxy.getOrderGuideOverviewByCurrentUser()
            .pipe(
                tap((result: OrderGuideSimpleDto[]) => {
                    this.setBrands(result);
                })
            );
    }

    getOrderGuideTemplates$(
        filter?: string,
        skip?: number | 0,
        take?: number | 12): Observable<PagedResultDtoOfOrderGuideTemplateDto> {

        return this.orderGuideServiceProxy.getAllOrderGuideTemplate(
                filter
                , true
                , this.selectedBrandId
                , skip
                , take
            )
            .pipe(
                tap((result: PagedResultDtoOfOrderGuideTemplateDto) => {
                    this.setOrderGuideTemplates(result);
                })
            );
    }

    setOrderGuideTemplates(result: PagedResultDtoOfOrderGuideTemplateDto): void {
        //update items
        this._orderGuidesTemplates$.next(result.items);
    }

    selectOrderGuideTemplate(selectedTemplate: OrderGuideTemplateDto): void {
        if (!this._selectedOrderGuideTemplate || this._selectedOrderGuideTemplate.id !== selectedTemplate.id) {
            this._selectedOrderGuideTemplate = selectedTemplate;
            this._selectedOrderGuideTemplate$.next(selectedTemplate);
            this._setOrderGuideTemplate(selectedTemplate);

            return;
        }

        this.unselectBrand();
    }

    unselectOrderGuideTemplate(): void {
        abp.utils.setCookieValue(this.selectedTemplateCacheKey, '', new Date(0), abp.appPath);
        sessionStorage.removeItem(this.selectedTemplateCacheKey);

        this._selectedOrderGuideTemplate = undefined;
        this._selectedOrderGuideTemplate$.next(undefined);
    }

    private _checkIfallowNavigateToOrderConfirmation(result: PagedResultDtoOfArticleDto) {
        if (result.totalCount === 0) {
            this.isAllowNavigateToOrderConfirmation = true;
        }
    }

    getLocalWarehouse$(
        userId?: number | undefined,
        skip?: number | 0,
        take?: number | 12): Observable<PagedResultDtoOfArticleDto> {

        return this.webShopServiceProxy.getLocalWarehouse(
            userId
            , skip
            , take
        )
            .pipe(
                tap((result: PagedResultDtoOfArticleDto) => {
                    this._checkIfallowNavigateToOrderConfirmation(result);
                })
            );
    }

    confirmAndSignOrder$(input: IConfirmWebshopOrderInputDto): Observable<WebshopSendBasketOutput> {
        return this.webShopServiceProxy.confirmAndSignOrder(new ConfirmWebshopOrderInputDto(input))
            .pipe(
                tap((result: WebshopSendBasketOutput) => {
                    //this._checkIfallowNavigateToOrderConfirmation(result);
                })
            );
    }

    goHome() {
        if (window.location.pathname === this.fieldAgentDashboard) {
            abp.message.confirm(
                'Do you want to go back to your main account?'
                , 'Stop impersonation'
                , (result: boolean) => {
                    if (result) {
                        this.flush();
                        this.impersonationService.backToImpersonator();
                    }
                }
                , {
                    showCloseButton: true,
                    customClass: {
                        container: 'modal-like'
                    }
                });
        } else {
            if (this.orderStateService.getCurrentState() === OrderState.SelectTemplates) {
                this.navigateToSelectTemplate();
                return;
            }

            let opts = {
                showDenyButton: true,
                showCloseButton: true,
                showCancelButton: true,
                confirmButtonText: 'Proceed',
                denyButtonText: 'Continue order process',
                cancelButtonText: 'Cancel',
                customClass: {
                    container: 'modal-like three-action',
                    actions: 'my-actions',
                    cancelButton: 'order-1 right-gap',
                    confirmButton: 'order-2',
                },
            };

            this.confirmService.confirmWithThreeStates(
                'Proceeding will clear the current order.'
                , 'You are trying to leave current order process?'
                , (result) => {
                    if (result.isConfirmed) {
                        this.flush();
                        this.router.navigate([this.fieldAgentDashboard]);
                    } else if (result.isDenied) {
                        switch (this.orderStateService.getCurrentState()) {
                            case OrderState.SelectTemplates: this.navigateToSelectTemplate(); break;
                            case OrderState.SelectProducts: this.navigateToSelectProducts(); break;
                            case OrderState.CheckCart: this.navigateToBasket(); break;
                            case OrderState.SelectPromotion: this.navigateToSelectPromotion(); break;
                            case OrderState.VerifyPromotion: this.navigateToVerifyPromotion(); break;
                            case OrderState.ConfirmOrder: this.navigateToConfirmOrder(); break;
                        }
                    } else if (result.isDismissed) {
                    }
                }
                , opts);
        }
    }

    navigateToBasket() {
        this.router.navigate([this.basketOverview]);
    }
    navigateToSelectTemplate() {
        this.router.navigate([this.fieldAgentDashboard]);
    }
    navigateToSelectProducts() {
        this.router.navigate([`${this.selectProducts}${this.selectedBrand.id}`]);
    }
    navigateToSelectPromotion() {
        this.router.navigate([this.selectPromotion]);
    }
    navigateToVerifyPromotion() {
        this.navigateToBasket();
    }
    navigateToConfirmOrder() {
        this.router.navigate([this.orderConfirmation]);
    }

    getBreadCrumbs() {
        const home = {icon: 'pi pi-home', url: this.fieldAgentDashboard};

        return [
            home
            , { label: this.selectedBrandValue?.name}
            , { label: this.selectedOrderGuideTemplatedValue?.name }
        ];
    }
}

